import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { FileSpreadsheet, Download, AlertTriangle, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  ['all', 'Todos los estados'],
  ['activa', 'Activa'],
  ['en_taller', 'En Taller'],
  ['en revisión', 'En Revisión'],
  ['rota', 'Rota'],
  ['prestada', 'Prestada'],
  ['vendida', 'Vendida'],
  ['retirada', 'Retirada'],
  ['robada', 'Robada'],
];

export default function LokkiExportModal({ open, onClose, bicycles = [], preSelectedIds = [] }) {
  const [models, setModels] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [statusFilter, setStatusFilter] = useState('activa');
  const [selectedModels, setSelectedModels] = useState([]); // empty = all models
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [onlySelected, setOnlySelected] = useState(false);
  const [includePrices, setIncludePrices] = useState(true);
  const [checked, setChecked] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/catalog/models`).then(r => setModels(r.data || [])).catch(() => {});
    axios.get(`${API}/catalog/seasons`).then(r => setSeasons(r.data || [])).catch(() => {});
    const hasSel = preSelectedIds.length > 0;
    setOnlySelected(hasSel);
    setStatusFilter(hasSel ? 'all' : 'activa');
    setSelectedModels([]);
    setCategoryFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const findModel = useCallback((b) => models.find(m =>
    (b.modelo_id && m.id === b.modelo_id) ||
    (b.numero_modelo != null && m.numero_modelo === b.numero_modelo) ||
    (b.model && m.nombre_modelo === b.model)
  ), [models]);

  const effCat = useCallback((b) => b.category || findModel(b)?.categoria || '', [findModel]);

  const DAYS = useMemo(() => Array.from({ length: 14 }, (_, i) => i + 1), []);
  const currentSeason = useMemo(() => {
    const now = new Date();
    const mmdd = `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return seasons.find(s => (s.date_from <= s.date_to)
      ? (mmdd >= s.date_from && mmdd <= s.date_to)
      : (mmdd >= s.date_from || mmdd <= s.date_to)) || null;
  }, [seasons]);
  const seasonFactor = 1 + ((parseFloat(currentSeason?.pct_increment) || 0) / 100);

  const dayPrice = useCallback((b, n) => {
    const m = findModel(b);
    const bdp = m?.base_day_prices || {};
    const disabled = currentSeason && (m?.disabled_seasons || []).includes(currentSeason.id);
    const factor = disabled ? 1 : seasonFactor;
    for (const k of Object.keys(bdp)) {
      const [f, t2] = k.split('-').map(Number);
      if (!isNaN(f) && !isNaN(t2) && n >= f && n <= t2) {
        const base = parseFloat(bdp[k]);
        if (!isNaN(base)) return Math.round(base * factor * 100) / 100;
      }
    }
    return '';
  }, [findModel, seasonFactor, currentSeason]);

  const modelNames = useMemo(() => [...new Set(bicycles.map(b => b.model).filter(Boolean))].sort(), [bicycles]);
  const categories = useMemo(() => [...new Set(bicycles.map(effCat).filter(Boolean))].sort(), [bicycles, effCat]);

  const toggleModel = (name) => setSelectedModels(prev =>
    prev.includes(name) ? prev.filter(m => m !== name) : [...prev, name]);

  const filtered = useMemo(() => bicycles.filter(b => {
    if (onlySelected && !preSelectedIds.includes(b.id)) return false;
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (selectedModels.length > 0 && !selectedModels.includes(b.model)) return false;
    if (categoryFilter === '__none__') { if (effCat(b)) return false; }
    else if (categoryFilter !== 'all' && effCat(b) !== categoryFilter) return false;
    return true;
  }), [bicycles, onlySelected, preSelectedIds, statusFilter, selectedModels, categoryFilter, effCat]);

  const filteredKey = filtered.map(b => b.id).join(',');
  useEffect(() => { setChecked(new Set(filtered.map(b => b.id))); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredKey, open]);

  const toggleOne = (id) => setChecked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allChecked = filtered.length > 0 && filtered.every(b => checked.has(b.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(filtered.map(b => b.id)));

  const rows = filtered.filter(b => checked.has(b.id));
  const hasHeight = rows.some(b => b.recommended_height);
  const missingCat = rows.filter(b => !effCat(b)).length;
  const showPrices = includePrices;

  const exportXlsx = () => {
    if (rows.length === 0) return toast.error('No hay bicicletas seleccionadas para exportar');
    const header = ['Code/Serial Number', 'Category', 'Model', 'Size'];
    if (hasHeight) header.push('Recommended Height (cm)');
    if (showPrices) DAYS.forEach(d => header.push(`Day ${d}`));
    const aoa = [header, ...rows.map(b => {
      const r = [String(b.number ?? ''), effCat(b) || '', b.model || '', String(b.size ?? '')];
      if (hasHeight) r.push(b.recommended_height ? String(b.recommended_height) : '');
      if (showPrices) DAYS.forEach(d => {
        const p = dayPrice(b, d);
        r.push(p === '' ? '' : Number(p));
      });
      return r;
    })];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = header.map((_, i) => ({ wch: i === 0 ? 20 : 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bicycles');
    XLSX.writeFile(wb, `lokki_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${rows.length} bicicleta(s) exportada(s) para Lokki`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-green-600" />
            Exportar a Lokki (.xlsx)
          </DialogTitle>
          <DialogDescription>
            Vista previa exacta del Excel. Los datos se exportan tal cual están guardados, sin ceros añadidos.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9" data-testid="lokki-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Modelos (varios)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 w-full justify-between font-normal" data-testid="lokki-model-filter">
                  <span className="truncate">
                    {selectedModels.length === 0
                      ? 'Todos los modelos'
                      : selectedModels.length === 1
                        ? selectedModels[0]
                        : `${selectedModels.length} modelos`}
                  </span>
                  <ChevronDown size={14} className="opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2 max-h-72 overflow-y-auto" align="start">
                <label className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm font-medium">
                  <Checkbox checked={selectedModels.length === 0} onCheckedChange={() => setSelectedModels([])} data-testid="lokki-model-all" />
                  Todos los modelos
                </label>
                {modelNames.map(m => (
                  <label key={m} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
                    <Checkbox checked={selectedModels.includes(m)} onCheckedChange={() => toggleModel(m)} data-testid={`lokki-model-opt-${m}`} />
                    <span className="truncate">{m}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Categoría</Label>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9" data-testid="lokki-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                <SelectItem value="__none__">Sin categoría</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {preSelectedIds.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={onlySelected} onCheckedChange={(v) => setOnlySelected(!!v)} data-testid="lokki-only-selected" />
              Solo las {preSelectedIds.length} bicicleta(s) seleccionadas en la lista
            </label>
          )}
          {seasons.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={includePrices} onCheckedChange={(v) => setIncludePrices(!!v)} data-testid="lokki-include-prices" />
              Incluir precios Day 1–14 (temporada actual: {currentSeason ? `${currentSeason.nombre} +${parseFloat(currentSeason.pct_increment) || 0}%` : 'base'})
            </label>
          )}
        </div>

        {missingCat > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="lokki-missing-cat-warning">
            <AlertTriangle size={15} className="shrink-0" />
            {missingCat} bicicleta(s) sin categoría. Asígnala en la ficha de la bici o configura el mapeo por modelo en "Modelos".
          </div>
        )}

        {/* Excel preview */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-sm" data-testid="lokki-preview-table">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="p-2 w-8">
                    <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="lokki-check-all" />
                  </th>
                  <th className="p-2 text-left font-semibold text-slate-700 whitespace-nowrap">Code/Serial Number</th>
                  <th className="p-2 text-left font-semibold text-slate-700">Category</th>
                  <th className="p-2 text-left font-semibold text-slate-700">Model</th>
                  <th className="p-2 text-left font-semibold text-slate-700">Size</th>
                  {hasHeight && <th className="p-2 text-left font-semibold text-slate-700 whitespace-nowrap">Recommended Height (cm)</th>}
                  {showPrices && DAYS.map(d => (
                    <th key={d} className="p-2 text-left font-semibold text-slate-700 whitespace-nowrap">Day {d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={99} className="p-6 text-center text-slate-400">No hay bicicletas con los filtros actuales</td></tr>
                ) : filtered.map(b => (
                  <tr key={b.id} className={`border-t border-slate-100 ${checked.has(b.id) ? '' : 'opacity-40'}`} data-testid={`lokki-row-${b.id}`}>
                    <td className="p-2"><Checkbox checked={checked.has(b.id)} onCheckedChange={() => toggleOne(b.id)} data-testid={`lokki-check-${b.id}`} /></td>
                    <td className="p-2 font-mono">{String(b.number ?? '')}</td>
                    <td className="p-2">{effCat(b) || <span className="text-amber-600 text-xs">— sin categoría —</span>}</td>
                    <td className="p-2">{b.model}</td>
                    <td className="p-2">{b.size}</td>
                    {hasHeight && <td className="p-2">{b.recommended_height || ''}</td>}
                    {showPrices && DAYS.map(d => (
                      <td key={d} className="p-2">{dayPrice(b, d)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <Badge variant="outline" className="text-slate-600" data-testid="lokki-count">{rows.length} fila(s) a exportar</Badge>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={exportXlsx} disabled={rows.length === 0} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="lokki-download-btn">
              <Download size={16} />
              Descargar Excel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
